import { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useLocation, Navigate } from 'react-router-dom';
import './App.css';
import logo from './assets/images/logosansbg.png';
import Home from './pages/Home';
import Shop from './pages/Shop';
import Admin from './pages/Admin';
import AccessDenied from './pages/AccessDenied';
import ProductDetail from './pages/ProductDetail';
import Account from './pages/Account';
import AccountVerification from './pages/AccountVerification';
import Auth from './pages/Auth';
import PaymentInfo from './pages/PaymentInfo';
import CartDrawer from './components/CartDrawer';
import { CartProvider, useCart } from './context/CartContext';
import { AuthProvider, useAuth } from './context/AuthContext';

const socialLinks = [
  {
    label: 'Facebook',
    href: 'https://www.facebook.com/people/Unisport-Europe-Officiel/61579959819674/?_rdr',
    icon: (
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <path
          fill="currentColor"
          d="M15 3h3V0h-3c-3.3 0-6 2.7-6 6v3H6v4h3v11h4V13h3l1-4h-4V6c0-.6.4-1 1-1z"
        />
      </svg>
    )
  },
  {
    label: 'YouTube',
    href: 'https://www.youtube.com/watch?v=hhtLnODEu7k&list=RDhhtLnODEu7k&start_radio=1',
    icon: (
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <path
          fill="currentColor"
          d="M23 7.2s-.2-1.6-.8-2.3c-.7-.8-1.5-.8-1.9-.9C17.5 3.5 12 3.5 12 3.5h0s-5.5 0-8.3.5c-.4.1-1.2.1-1.9.9C1.2 5.6 1 7.2 1 7.2S.8 9.1.8 11v2c0 1.9.2 3.8.2 3.8s.2 1.6.8 2.3c.7.8 1.6.8 2 1 1.5.1 8.2.5 8.2.5s5.5 0 8.3-.5c.4-.1 1.2-.1 1.9-.9.6-.7.8-2.3.8-2.3s.2-1.9.2-3.8V11c0-1.9-.2-3.8-.2-3.8zM10 15.5v-7l6 3.5-6 3.5z"
        />
      </svg>
    )
  },
  {
    label: 'TikTok',
    href: 'https://www.tiktok.com',
    icon: (
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <path
          fill="currentColor"
          d="M20 7.4c-1.5 0-2.9-.4-4.1-1.2v6.3c0 3-2.4 5.5-5.5 5.5S5 15.5 5 12.4s2.4-5.5 5.5-5.5c.3 0 .6 0 .9.1v3.2c-.3-.1-.6-.1-.9-.1-1.3 0-2.4 1.1-2.4 2.3 0 1.3 1.1 2.3 2.4 2.3 1.2 0 2.2-.9 2.3-2.1V2h3a5.9 5.9 0 0 0 4.2 1.7v3.7z"
        />
      </svg>
    )
  }
];

const keyLinks = [
  { label: 'Accueil', to: '/' },
  { label: 'Boutique', to: '/boutique' },
  { label: 'Histoire', to: '/#histoire' }
];

const useIsHeroPage = () => {
  const location = useLocation();
  return location.pathname === '/';
};

function AppShell() {
  const [cartOpen, setCartOpen] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [alertState, setAlertState] = useState({ open: false, message: '' });
  const { count, clearCart } = useCart();
  const { user, isAdmin, logout } = useAuth();
  const location = useLocation();
  const isHeroPage = useIsHeroPage();
  const isAuthenticated = !!user;

  function AdminRoute({ children }) {
    const { isAdmin, loading: authLoading } = useAuth();
    const location = useLocation();
    if (authLoading) return null;
    if (!isAdmin) {
      return <Navigate to="/forbidden" state={{ from: location }} replace />;
    }
    return children;
  }

  useEffect(() => {
    if (location.hash) {
      const target = document.querySelector(location.hash);
      if (target) {
        target.scrollIntoView({ behavior: 'smooth' });
        return;
      }
    }
    window.scrollTo({ top: 0 });
  }, [location]);

  useEffect(() => {
    setIsMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const originalAlert = window.alert;
    window.alert = (message = '') => {
      setAlertState({
        open: true,
        message: typeof message === 'string' ? message : String(message)
      });
    };
    return () => {
      window.alert = originalAlert;
    };
  }, []);

  const closeAlert = () => setAlertState({ open: false, message: '' });

  const handleLogout = async () => {
    await logout();
    clearCart();
    setIsMenuOpen(false);
  };

  return (
    <>
      <div className="app-shell">
        <header className={`site-header${isHeroPage ? ' site-header--hero' : ''}`}>
          <div className="site-header__brand">
            <Link to="/">
              <img src={logo} alt="Unisport du Haut-Nkam" />
            </Link>
            <div className="site-header__brand-text">
              <p className="site-header__club">Unisport du Haut-Nkam</p>
              <small>02 octobre 1958</small>
            </div>
          </div>
          <button
            type="button"
            className="site-header__nav-toggle"
            onClick={() => setIsMenuOpen((prev) => !prev)}
            aria-label="Ouvrir le menu"
          >
            ☰
          </button>
          <nav className={`site-header__nav ${isMenuOpen ? 'is-open' : ''}`}>
            {keyLinks.map((link) =>
              link.to.startsWith('/#') ? (
                <a
                  key={link.label}
                  href={link.to}
                  onClick={() => setIsMenuOpen(false)}
                >
                  {link.label}
                </a>
              ) : (
                <Link key={link.label} to={link.to} onClick={() => setIsMenuOpen(false)}>
                  {link.label}
                </Link>
              )
            )}
            {isAdmin && (
              <Link to="/admin" onClick={() => setIsMenuOpen(false)}>
                Admin
              </Link>
            )}
            {isAuthenticated ? (
              <div className="account-actions">
                <Link className="site-header__nav-link-button" to="/compte" onClick={() => setIsMenuOpen(false)}>
                  Mon compte
                </Link>
                <button
                  type="button"
                  className="site-header__nav-link-button site-header__logout"
                  onClick={() => {
                    handleLogout();
                    setIsMenuOpen(false);
                  }}
                >
                  Déconnexion
                </button>
              </div>
            ) : (
              <Link className="site-header__nav-link-button" to="/connexion" onClick={() => setIsMenuOpen(false)}>
                Se connecter
              </Link>
            )}
            <button
              type="button"
              className="site-header__cta site-header__cta--ghost cart-button"
              onClick={() => {
                setCartOpen(true);
                setIsMenuOpen(false);
              }}
            >
              Panier
              {count > 0 && <span className="cart-button__badge">{count}</span>}
            </button>
          </nav>
        </header>

        <main>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/boutique" element={<Shop />} />
            <Route path="/admin" element={<AdminRoute><Admin /></AdminRoute>} />
            <Route path="/forbidden" element={<AccessDenied />} />
            <Route path="/verification" element={<AccountVerification />} />
            <Route path="/compte" element={<Account />} />
            <Route path="/connexion" element={<Auth />} />
            <Route path="/produits/:productId" element={<ProductDetail />} />
            <Route path="/paiements" element={<PaymentInfo />} />
          </Routes>
        </main>

        <footer className="site-footer">
          <div>
            <h4>Contact</h4>
            <p>
              <a href="tel:+33650326167" className="contact-link">+33 6 50 32 61 67</a>
              <a
                href="https://wa.me/33650326167"
                target="_blank"
                rel="noopener noreferrer"
                className="contact-whatsapp"
                aria-label="WhatsApp"
              >
                <svg viewBox="0 0 24 24" aria-hidden="true">
                  <path
                    fill="currentColor"
                    d="M12 2a9.9 9.9 0 0 0-6.995 16.903L4 22l3.221-.986A9.9 9.9 0 1 0 12 2zm0 1.8a8.1 8.1 0 0 1 5.729 13.838l-.286.278-.036.048a.9.9 0 0 0-.164.307l-.018.054-.54 1.632-1.681-.521-.052-.016a.9.9 0 0 0-.644.05l-.065.033-.067.025a8.1 8.1 0 1 1-2.176-15.468z"
                  />
                  <path
                    fill="currentColor"
                    d="M9.27 7.706c-.205-.462-.42-.471-.617-.48l-.527-.01c-.18.002-.472.068-.718.34s-.94.917-.94 2.237.963 2.593 1.096 2.773c.134.179 1.85 2.847 4.567 3.878 2.252.885 2.711.709 3.202.664.491-.045 1.576-.641 1.798-1.261.222-.62.222-1.151.155-1.261-.066-.11-.245-.178-.512-.313s-1.576-.777-1.819-.865c-.243-.089-.42-.134-.598.133-.179.268-.689.865-.845 1.043-.155.179-.311.2-.578.066-.268-.133-1.12-.413-2.133-1.316-.79-.704-1.322-1.573-1.477-1.84-.155-.268-.016-.413.117-.546.12-.12.268-.312.4-.467.133-.155.178-.268.268-.446.089-.178.044-.334-.022-.467-.066-.134-.588-1.423-.825-1.949z"
                  />
                </svg>
              </a>
            </p>
            <p>
              <a href="mailto:contact@unisportduhautnkam.com" className="contact-link">
                contact@unisportduhautnkam.com
              </a>
            </p>
          </div>
          <div>
            <h4>Points de retrait</h4>
            <p>Paris</p>
            <p>
              <a
                href="https://www.google.com/maps/dir/?api=1&destination=40+Rue+Championnet+75018+Paris"
                target="_blank"
                rel="noopener noreferrer"
              >
                40 Rue Championnet 75018 Paris
              </a>
            </p>
          </div>
          <div>
            <h4>Besoin d’aide</h4>
            <p>
              <Link to="/paiements">Nos moyens de paiement</Link>
            </p>
            <p>Gérer mes données personnelles</p>
          </div>
          <div>
            <h4>Suivez-nous</h4>
            <div className="site-footer__socials">
              {socialLinks.map((link) => (
                <a key={link.label} href={link.href} target="_blank" rel="noopener noreferrer" aria-label={link.label}>
                  {link.icon}
                </a>
              ))}
            </div>
          </div>
        </footer>
        <div className="site-footer__legal">
          © {new Date().getFullYear()} Unisport du Haut-Nkam — Tous droits réservés.
          <button type="button" className="site-footer__top-button site-footer__top-button--icon" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
            <span>Haut de page</span>
            <span aria-hidden="true">↑</span>
          </button>
        </div>
        {alertState.open && (
          <div className="alert-modal" role="alertdialog" aria-modal="true" aria-live="assertive">
            <div className="alert-modal__overlay" onClick={closeAlert} aria-hidden="true" />
            <div className="alert-modal__dialog">
              <div className="alert-modal__body">
                <p>{alertState.message}</p>
                <button type="button" onClick={closeAlert}>
                  Fermer
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
      <CartDrawer isOpen={cartOpen} onClose={() => setCartOpen(false)} />
    </>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <CartProvider>
        <Router>
          <AppShell />
        </Router>
      </CartProvider>
    </AuthProvider>
  );
}
